import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Check,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";


export default function MachineMeterModal({
  open,
  machines = [],
  initialMachineId = "",
  saving = false,
  onClose,
  onSubmit,
}) {
  const [machineId, setMachineId] =
    useState("");

  const [hours, setHours] =
    useState("");

  const [note, setNote] =
    useState("");


  const selectedMachine =
    useMemo(
      () =>
        machines.find(
          (machine) =>
            machine.id ===
            machineId
        ) || null,
      [
        machineId,
        machines,
      ]
    );


  useEffect(() => {
    if (!open) {
      return;
    }

    const selectedId =
      initialMachineId ||
      machines[0]?.id ||
      "";

    const machine =
      machines.find(
        (item) =>
          item.id === selectedId
      );

    setMachineId(selectedId);

    setHours(
      machine?.operatingHours ??
      0
    );

    setNote("");
  }, [
    initialMachineId,
    machines,
    open,
  ]);


  useEffect(() => {
    if (
      !open ||
      !selectedMachine
    ) {
      return;
    }

    setHours(
      selectedMachine.operatingHours ??
      0
    );
  }, [
    machineId,
    open,
    selectedMachine,
  ]);


  if (!open) {
    return null;
  }


  const submit = (
    event
  ) => {
    event.preventDefault();

    if (!machineId) {
      toast.error(
        "Sélectionnez une machine."
      );

      return;
    }

    const value = Number(hours);

    if (
      Number.isNaN(value) ||
      value < 0
    ) {
      toast.error(
        "Le compteur d’heures est invalide."
      );

      return;
    }

    if (
      selectedMachine &&
      value <
        Number(
          selectedMachine.operatingHours ||
          0
        )
    ) {
      toast.error(
        "Le compteur ne peut pas diminuer."
      );

      return;
    }

    onSubmit(
      machineId,
      {
        operating_hours: value,
        note: note.trim(),
      }
    );
  };


  const inputClass =
    "w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm";


  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--do-overlay)] p-4"
      data-testid="machine-meter-modal"
    >
      <form
        onSubmit={submit}
        className="acta-modal-surface w-full max-w-lg rounded-2xl bg-[var(--do-bg-solid)] p-5 shadow-2xl sm:p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="label-eyebrow">
              Compteur machine
            </div>

            <h2 className="mt-1 font-heading text-xl font-black">
              Mettre à jour les heures
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-[var(--do-border)] p-2"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="label-eyebrow mb-1.5 block">
              Machine
            </label>

            <select
              value={machineId}
              onChange={(event) =>
                setMachineId(
                  event.target.value
                )
              }
              disabled={saving}
              data-testid="machine-meter-machine"
              className={inputClass}
            >
              <option value="">
                Sélectionner une machine
              </option>

              {machines.map(
                (machine) => (
                  <option
                    key={machine.id}
                    value={machine.id}
                  >
                    {machine.name}
                    {" — "}
                    {machine.serialNumber}
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label className="label-eyebrow mb-1.5 block">
              Heures de fonctionnement
            </label>

            <input
              type="number"
              min="0"
              step="0.01"
              value={hours}
              onChange={(event) =>
                setHours(
                  event.target.value
                )
              }
              disabled={saving}
              data-testid="machine-meter-hours"
              className={inputClass}
            />

            <div className="mt-1 text-xs text-[var(--do-muted)]">
              Valeur actuelle :{" "}
              <strong>
                {selectedMachine
                  ?.operatingHours ??
                  0} h
              </strong>
            </div>
          </div>

          <div>
            <label className="label-eyebrow mb-1.5 block">
              Note
            </label>

            <textarea
              value={note}
              onChange={(event) =>
                setNote(
                  event.target.value
                )
              }
              disabled={saving}
              rows={3}
              maxLength={1000}
              className={`${inputClass} resize-y`}
              placeholder="Relevé compteur, source ou commentaire…"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2 border-t border-[var(--do-border)] pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-[var(--do-border)] px-4 py-2 text-sm font-heading font-semibold"
          >
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            data-testid="machine-meter-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--do-accent)] px-4 py-2 text-sm font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50"
          >
            <Check
              size={17}
              weight="bold"
            />

            {saving
              ? "Enregistrement…"
              : "Enregistrer le compteur"}
          </button>
        </div>
      </form>
    </div>
  );
}
